import { Fragment, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useForm, Controller } from "react-hook-form";
import { Helmet } from "react-helmet-async";
import { useHistory, useParams } from "react-router-dom";
import swal from "@sweetalert/with-react";
import Button from "../../components/Button";
import Card from "../../components/Card";
import TextInput from "../../components/TextInput";
import Dropzone from "../../components/Dropzone";
import Loader from "../../components/Loader";
import BigLoader from "../../components/BigLoader";
import { setInterface } from "../../redux/slices/interfaceSlice";
import service from "../../service";

export default function ComposeDeepRead({ editMode = false }) {
  const dispatch = useDispatch();
  const { id } = useParams();
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [loadingContent, setLoadingContent] = useState(editMode);

  const {
    register,
    handleSubmit,
    control,
    watch,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: {
      title: "",
      price: 0,
      cover: null,
      show_on_homepage: false,
    },
  });

  const _proceed = ({ title, price, cover, show_on_homepage }) => {
    setLoading(true);
    const formData = new FormData();
    formData.append("title", title);
    const priceNum = Number.isFinite(Number(price)) ? Number(price) : 0;
    formData.append("price", String(priceNum));
    formData.append("show_on_homepage", show_on_homepage ? "true" : "false");
    if (cover instanceof File) {
      formData.append("cover", cover);
    }

    service({
      url: editMode ? "/api/deep-read/" + id : "/api/deep-read",
      data: formData,
      method: editMode ? "PUT" : "POST",
      headers: { "Content-Type": "multipart/form-data" },
    })
      .then((response) => {
        setLoading(false);
        swal(
          "Berhasil",
          editMode ? "Deep Read berhasil disunting" : "Deep Read berhasil ditambah",
          "success"
        ).then(() => {
          if (!editMode && response.data?.id) {
            history.replace(`/deep-read/${response.data.id}/book`);
          } else {
            history.replace("/deep-read");
          }
        });
      })
      .catch((e) => {
        setLoading(false);
        const msg =
          e?.response?.data?.message ||
          e?.response?.data?.errors?.[0]?.message ||
          "Gagal menyimpan. Coba lagi.";
        swal("Gagal", String(msg), "error");
      });
  };

  useEffect(() => {
    dispatch(
      setInterface({
        active: "Deep Read",
        pageTitle: editMode ? "Edit Deep Read" : "Tambah Deep Read",
      })
    );
  }, [dispatch, editMode]);

  useEffect(() => {
    if (!editMode) {
      setLoadingContent(false);
      return;
    }
    service
      .get("/api/deep-read/" + id)
      .then((response) => {
        const { title, price, cover, show_on_homepage } = response.data;
        setLoadingContent(false);
        reset({
          title: title || "",
          price: price ?? 0,
          cover: cover || null,
          show_on_homepage: !!show_on_homepage,
        });
      })
      .catch((e) => {
        setLoadingContent(false);
        if (e.response?.status === 404) {
          history.replace("/deep-read");
        }
      });
  }, [editMode, id, history, reset]);

  if (loadingContent) {
    return (
      <div className="h-64 flex justify-center items-center flex-col">
        <Loader className="bg-black-100" />
        <span className="mt-3 text-black-300 text-sm">Memuat</span>
      </div>
    );
  }

  return (
    <form className="flex flex-col lg:flex-row" onSubmit={handleSubmit(_proceed)}>
      <Helmet>
        <title>{editMode ? "Edit Deep Read" : "Tambah Deep Read"}</title>
      </Helmet>
      <Card className="flex-1">
        <TextInput
          type="text"
          label="Judul"
          containerClassName="mb-4"
          message={errors.title?.message}
          {...register("title", { required: "Tidak boleh kosong" })}
        />
        <TextInput
          type="number"
          label="Harga (Rp)"
          containerClassName="mb-4"
          {...register("price", { valueAsNumber: true })}
        />
        <Button type="submit" className="w-28 hidden lg:block">
          {editMode ? "Simpan" : "Tambah"}
        </Button>
      </Card>
      <div className="w-auto lg:w-5/12 ml-0 mt-5 lg:ml-5 lg:mt-0">
        <Card className="mb-5">
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input type="checkbox" className="w-4 h-4" {...register("show_on_homepage")} />
            <span className="text-sm font-semibold text-black-200">Tampilkan di Homepage</span>
          </label>
        </Card>
        <Card>
          <Controller
            control={control}
            name="cover"
            render={({ field: { onChange } }) => (
              <Dropzone
                dropzone={{ accept: "image/jpeg, image/png" }}
                message={errors.cover?.message}
                label="Gambar Cover (opsional)"
                onChange={(accepted) => onChange(accepted[0])}
                containerClassName="mb-4"
              />
            )}
          />
          <Fragment>
            <span className="text-sm font-semibold text-black-300 mb-2 block">Preview</span>
            <div className="flex justify-center">
              <img
                src={
                  watch("cover") instanceof File
                    ? URL.createObjectURL(watch("cover"))
                    : watch("cover")
                    ? process.env.REACT_APP_BASE_URL + watch("cover")
                    : `${process.env.REACT_APP_BASE_URL}/no-image.png`
                }
                alt="Preview Cover"
                className="w-28 h-28 object-cover bg-white-200"
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = `${process.env.REACT_APP_BASE_URL}/no-image.png`;
                }}
              />
            </div>
          </Fragment>
        </Card>
        <Card className="block lg:hidden mt-5">
          <Button type="submit" className="w-28">
            {editMode ? "Simpan" : "Tambah"}
          </Button>
        </Card>
      </div>
      {loading && <BigLoader />}
    </form>
  );
}
